import { apiSlice } from '@/store/api/apiSlice';

export interface GeneralPredictionResponse {
  status: 'PENDING' | 'SUCCESS' | 'FAILED';
  prediction: string;
  videoId?: string | null;
  id?: string;
}

export interface GeneralPredictionRequest {
  userId: string;
}

export interface SyncPredictionRequest {
  predictionId: string;
  language: string;
  content?: Record<string, any>;
  videoUrl?: string;
  audioUrl?: string;
}

export interface SyncPredictionResponse {
  status?: number;
  message: string | string[];
  error?: string;
  statusCode?: number;
}

export interface GenerateAudioRequest {
  predictionId: string;
  language: string;
}

export interface GenerateAudioResponse {
  status: string;
  message?: string;
}

export interface GetAudioRequest {
  predictionId: string;
  language: string;
}

export interface GetAudioResponse {
  status: string;
  url?: string;
  message?: string;
}

export interface AudioItem {
  id: string;
  predictionId: string;
  language: string;
  audioStatus: string;
  audioUrl: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface ListAudiosResponse {
  data: AudioItem[];
  total: number;
  skip: number;
  take: number;
}

export interface ListAudiosRequest {
  skip: number;
  take: number;
  search?: string;
  userId?: string;
  language?: string;
  audioStatus?: string;
}

export interface DeleteAudioResponse {
  predictionId: string;
  deletedCount: number;
  message: string;
}

export const generalPredictionApi = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    getGeneralPrediction: builder.query<GeneralPredictionResponse, GeneralPredictionRequest>({
      query: ({ userId }) => ({
        url: '/predictions/general',
        method: 'GET',
        params: {
          userId,
        },
      }),
    }),
    syncPrediction: builder.mutation<SyncPredictionResponse, SyncPredictionRequest>({
      query: (data) => ({
        url: '/predictions/sync-prediction',
        method: 'POST',
        body: data,
      }),
    }),
    generateAudio: builder.mutation<GenerateAudioResponse, GenerateAudioRequest>({
      query: (data) => ({
        url: '/predictions/generate-audio',
        method: 'POST',
        body: data,
      }),
    }),
    getPredictionAudio: builder.query<GetAudioResponse, GetAudioRequest>({
      query: ({ predictionId, language }) => ({
        url: `/predictions/${predictionId}/audio`,
        method: 'GET',
        params: {
          language,
        },
      }),
    }),
    listPredictionAudios: builder.query<ListAudiosResponse, ListAudiosRequest>({
      query: (params) => ({
        url: '/predictions/audios',
        method: 'GET',
        params,
      }),
    }),
    deletePredictionAudio: builder.mutation<DeleteAudioResponse, string>({
      query: (id) => ({
        url: `/predictions/audios/${id}`,
        method: 'DELETE',
      }),
    }),
  }),
  overrideExisting: false,
});

export const {
  useLazyGetGeneralPredictionQuery,
  useSyncPredictionMutation,
  useGenerateAudioMutation,
  useGetPredictionAudioQuery,
  useLazyGetPredictionAudioQuery,
  useListPredictionAudiosQuery,
  useDeletePredictionAudioMutation,
} = generalPredictionApi;
